import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Fade,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  LocalHospital as HospitalIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  MedicalServices as MedicalIcon,
  CalendarMonth as CalendarIcon,
  Psychology as PsychologyIcon,
  Mic as MicIcon,
  HeadsetMic as HeadsetMicIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { auth } from '../../firebase';
import ConsultaForm from './ConsultaForm';
import ConsultaVoz from './ConsultaVoz';
import TranscripcionConsulta from './TranscripcionConsulta';
import AnalizadorImagenes from './AnalizadorImagenes';
import AgendaCitas from './AgendaCitas';
import { NaraTypewriter, NaraTerminalCard } from './NaraTerminal';
import { publishNaraContext, clearNaraContext } from '../naraChatContext';
import AskNara from '../AskNara';

const API_URL = 'https://turismo.nara.chat/api';

// Transicion para diálogos
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMRDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados principales
  const [pacientes, setPacientes] = useState([]);
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [tabActual, setTabActual] = useState(0);

  // Estado para paciente seleccionado
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [expedienteActual, setExpedienteActual] = useState(null);
  const [consultasRecientes, setConsultasRecientes] = useState([]);

  // Diálogos
  const [dialogNuevoPaciente, setDialogNuevoPaciente] = useState(false);
  const [dialogNuevaConsulta, setDialogNuevaConsulta] = useState(false);
  const [dialogTranscripcion, setDialogTranscripcion] = useState(false);
  const [dialogAnalizadorImagenes, setDialogAnalizadorImagenes] = useState(false);
  const [dialogAgenda, setDialogAgenda] = useState(false);
  const [dialogConsultaVoz, setDialogConsultaVoz] = useState(false);
  const [datosVozParaConsulta, setDatosVozParaConsulta] = useState(null);
  const [naraBriefing, setNaraBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);

  // Configuración de documentos por país
  const documentosPorPais = {
    MX: { nombre: 'México', documento: 'CURP', placeholder: 'GARC850101HDFRRL09', maxLength: 18, pattern: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z][0-9]$' },
    CO: { nombre: 'Colombia', documento: 'Cédula de Ciudadanía', placeholder: '1234567890', maxLength: 12, pattern: '^[0-9]{6,12}$' },
    US: { nombre: 'Estados Unidos', documento: 'SSN', placeholder: '123-45-6789', maxLength: 11, pattern: '^[0-9]{3}-[0-9]{2}-[0-9]{4}$' },
    ES: { nombre: 'España', documento: 'DNI/NIE', placeholder: '12345678A', maxLength: 9, pattern: '^[0-9XYZ][0-9]{7}[A-Z]$' },
    PE: { nombre: 'Perú', documento: 'DNI', placeholder: '12345678', maxLength: 8, pattern: '^[0-9]{8}$' },
    AR: { nombre: 'Argentina', documento: 'DNI', placeholder: '12345678', maxLength: 8, pattern: '^[0-9]{7,8}$' },
    CL: { nombre: 'Chile', documento: 'RUT', placeholder: '12345678-9', maxLength: 10, pattern: '^[0-9]{7,8}-[0-9Kk]$' },
    EC: { nombre: 'Ecuador', documento: 'Cédula', placeholder: '1234567890', maxLength: 10, pattern: '^[0-9]{10}$' },
    VE: { nombre: 'Venezuela', documento: 'Cédula', placeholder: 'V-12345678', maxLength: 10, pattern: '^[VEJPvejp]-[0-9]{7,8}$' },
    OTHER: { nombre: 'Otro', documento: 'Documento de Identidad', placeholder: '', maxLength: 20, pattern: '.*' }
  };

  // Formulario nuevo paciente
  const [nuevoPaciente, setNuevoPaciente] = useState({
    nombre_completo: '',
    fecha_nacimiento: '',
    sexo: 'masculino',
    pais: 'CO',
    documento_identidad: '',
    telefono: '',
    correo_electronico: '',
    tipo_sangre: ''
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Autenticación
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u);
        const storedId = localStorage.getItem('user_id');
        if (storedId) {
          setUserId(storedId);
        } else {
          // Obtener user_id desde backend
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) {
              localStorage.setItem('user_id', data.id);
              setUserId(data.id);
            }
          } catch (err) {
            console.error('Error obteniendo usuario:', err);
          }
        }
      } else {
        navigate('/login');
      }
    });
    return () => { unsubscribe(); clearNaraContext(); };
  }, [navigate]);

  // Cargar pacientes
  const cargarPacientes = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busqueda) params.append('buscar', busqueda);
      params.append('limit', '50');

      const res = await fetch(`${API_URL}/emr/pacientes?${params}`, {
        headers: { 'X-User-Id': userId }
      });

      if (!res.ok) {
        if (res.status === 403) {
          setError('No tienes acceso al módulo de Expediente Médico. Contacta al administrador.');
          return;
        }
        throw new Error('Error al cargar pacientes');
      }

      const data = await res.json();
      setPacientes(data.pacientes || []);
      setTotalPacientes(data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, busqueda]);

  useEffect(() => {
    if (userId) {
      cargarPacientes();
    }
  }, [userId, cargarPacientes]);

  // Crear nuevo paciente
  const handleCrearPaciente = async () => {
    try {
      // Obtener empresa_id del usuario
      const userRes = await fetch(`${API_URL}/users/${userId}`, {
        headers: { 'X-User-Id': userId }
      });
      const userData = await userRes.json();

      // empresa_id está en userData.empresa.id (no en userData.company_id)
      const empresaId = userData.empresa?.id || userData.company_id;
      if (!empresaId) {
        throw new Error('No se pudo obtener el ID de la empresa del usuario');
      }

      // Preparar datos del paciente con el documento de identidad según el país
      const docConfig = documentosPorPais[nuevoPaciente.pais] || documentosPorPais.OTHER;
      const pacienteData = {
        nombre_completo: nuevoPaciente.nombre_completo,
        fecha_nacimiento: new Date(nuevoPaciente.fecha_nacimiento).toISOString(),
        sexo: nuevoPaciente.sexo,
        curp: nuevoPaciente.pais === 'MX' ? nuevoPaciente.documento_identidad : null,
        numero_seguro_social: null,
        tipo_sangre: nuevoPaciente.tipo_sangre || null,
        telefono: nuevoPaciente.telefono || null,
        correo_electronico: nuevoPaciente.correo_electronico || null,
        empresa_id: String(empresaId),
        // Guardar país y documento en dirección (campo JSONB flexible)
        direccion: {
          pais: nuevoPaciente.pais,
          pais_nombre: docConfig.nombre,
          tipo_documento: docConfig.documento,
          numero_documento: nuevoPaciente.documento_identidad
        }
      };

      const res = await fetch(`${API_URL}/emr/pacientes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify(pacienteData)
      });

      if (!res.ok) {
        const errData = await res.json();
        // Manejar errores de validación de FastAPI (array de detalles)
        let errorMsg = 'Error al crear paciente';
        if (errData.detail) {
          if (Array.isArray(errData.detail)) {
            errorMsg = errData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof errData.detail === 'string') {
            errorMsg = errData.detail;
          }
        }
        throw new Error(errorMsg);
      }

      const pacienteCreado = await res.json();
      setSnackbar({ open: true, message: 'Paciente creado correctamente', severity: 'success' });
      setDialogNuevoPaciente(false);
      setNuevoPaciente({
        nombre_completo: '',
        fecha_nacimiento: '',
        sexo: 'masculino',
        pais: 'CO',
        documento_identidad: '',
        telefono: '',
        correo_electronico: '',
        tipo_sangre: ''
      });
      cargarPacientes();

      // Crear expediente automáticamente
      await fetch(`${API_URL}/emr/expedientes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({
          paciente_id: pacienteCreado.id,
          empresa_id: String(empresaId)
        })
      });

    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Seleccionar paciente
  const handleSeleccionarPaciente = async (paciente) => {
    setPacienteSeleccionado(paciente);
    setTabActual(1);
    setNaraBriefing(null);

    // Publish context to NaraChat
    publishNaraContext('paciente', {
      id: paciente.id,
      nombre: paciente.nombre_completo,
      edad: paciente.fecha_nacimiento ? `${Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / 31557600000)} anos` : null,
      sexo: paciente.sexo,
      telefono: paciente.telefono,
      tipo_sangre: paciente.tipo_sangre,
    });

    try {
      // Obtener expediente
      const expRes = await fetch(`${API_URL}/emr/expedientes/paciente/${paciente.id}`, {
        headers: { 'X-User-Id': userId }
      });

      if (expRes.ok) {
        const exp = await expRes.json();
        setExpedienteActual(exp);

        // Obtener timeline
        const timeRes = await fetch(`${API_URL}/emr/expedientes/${exp.id}/timeline?limit=10`, {
          headers: { 'X-User-Id': userId }
        });
        if (timeRes.ok) {
          const timeData = await timeRes.json();
          setConsultasRecientes(timeData.timeline || []);
        }
      } else {
        setExpedienteActual(null);
        setConsultasRecientes([]);
      }

      // Cargar briefing NARA (en paralelo, no bloquea)
      setLoadingBriefing(true);
      fetch(`${API_URL}/emr/ia/briefing/${paciente.id}`, {
        headers: { 'X-User-Id': userId }
      }).then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setNaraBriefing(data); })
        .catch(() => {})
        .finally(() => setLoadingBriefing(false));

    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Renderizar lista de pacientes
  const renderListaPacientes = () => (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e3a5f' }}>
            Pacientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalPacientes} pacientes registrados
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogNuevoPaciente(true)}
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            borderRadius: 2,
            px: 3
          }}
        >
          Nuevo Paciente
        </Button>
      </Box>

      {/* Búsqueda */}
      <TextField
        fullWidth
        placeholder="Buscar paciente por nombre, CURP, teléfono..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          )
        }}
      />

      {/* Lista de pacientes */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : pacientes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography color="text.secondary">
            No hay pacientes registrados
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {pacientes.map((paciente) => (
            <Grid item xs={12} md={6} lg={4} key={paciente.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleSeleccionarPaciente(paciente)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#1e3a5f', mr: 2 }}>
                      {paciente.nombre_completo?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {paciente.nombre_completo}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {paciente.sexo} • {calcularEdad(paciente.fecha_nacimiento)} años
                      </Typography>
                    </Box>
                    {paciente.activo ? (
                      <Chip label="Activo" size="small" color="success" />
                    ) : (
                      <Chip label="Inactivo" size="small" color="default" />
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {paciente.telefono || 'Sin teléfono'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paciente.tipo_sangre || '—'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  // Acciones rápidas con diseño consistente
  const accionesRapidas = [
    {
      id: 'consulta-voz',
      nombre: 'Consulta por Voz',
      descripcion: 'Graba \u2192 IA \u2192 Consulta',
      icon: <MicIcon />,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      shadowColor: 'rgba(16, 185, 129, 0.4)',
      action: () => setDialogConsultaVoz(true)
    },
    {
      id: 'transcripcion',
      nombre: 'Transcribir Audio',
      descripcion: 'Audio \u2192 Notas SOAP',
      icon: <HeadsetMicIcon />,
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      shadowColor: 'rgba(99, 102, 241, 0.4)',
      action: () => setDialogTranscripcion(true)
    },
    {
      id: 'asistente-ia',
      nombre: 'Asistente IA',
      descripcion: 'Diagnósticos CIE-10',
      icon: <PsychologyIcon />,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
      shadowColor: 'rgba(139, 92, 246, 0.4)',
      action: () => setSnackbar({ open: true, message: 'Disponible dentro de Nueva Consulta', severity: 'info' })
    },
    {
      id: 'analisis-imagen',
      nombre: 'Análisis Imagen',
      descripcion: 'Rayos X, Dermato',
      icon: <ImageIcon />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      shadowColor: 'rgba(245, 158, 11, 0.4)',
      action: () => setDialogAnalizadorImagenes(true)
    },
    {
      id: 'nueva-consulta',
      nombre: 'Nueva Consulta',
      descripcion: 'Formulario completo',
      icon: <AssignmentIcon />,
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      shadowColor: 'rgba(239, 68, 68, 0.4)',
      action: () => setDialogNuevaConsulta(true)
    }
  ];

  // Glassmorphism base para paneles
  const glassPanel = (gradient) => ({
    p: 3,
    borderRadius: 4,
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0, left: 0, right: 0,
      height: 3,
      background: gradient
    }
  });

  // Renderizar vista de expediente
  const renderExpediente = () => (
    <Box>
      {/* Header con datos del paciente */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: 'radial-gradient(circle at 90% 20%, rgba(251, 191, 36, 0.15) 0%, transparent 50%), radial-gradient(circle at 10% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 40%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => { setTabActual(0); setPacienteSeleccionado(null); clearNaraContext(); }} sx={{ color: '#fff', mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar sx={{
              width: 64, height: 64, bgcolor: '#fbbf24', color: '#1e3a5f',
              fontSize: 24, fontWeight: 700, mr: 2,
              boxShadow: '0 4px 16px rgba(251, 191, 36, 0.4)'
            }}>
              {pacienteSeleccionado?.nombre_completo?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {pacienteSeleccionado?.nombre_completo}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {calcularEdad(pacienteSeleccionado?.fecha_nacimiento)} años • {pacienteSeleccionado?.sexo} • {pacienteSeleccionado?.tipo_sangre || 'Tipo de sangre N/D'}
                </Typography>
                <AskNara question={`Dame un resumen completo de ${pacienteSeleccionado?.nombre_completo}`} label="Resumen NARA" sx={{ border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(16,185,129,0.3)' } }} />
              </Box>
              {expedienteActual && (
                <Chip
                  icon={<DescriptionIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                  label={expedienteActual.numero_expediente}
                  size="small"
                  sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<MicIcon />}
              onClick={() => setDialogConsultaVoz(true)}
              size="small"
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.9)',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 12,
                '&:hover': { bgcolor: '#10b981' }
              }}
            >
              Consulta Voz
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogNuevaConsulta(true)}
              sx={{
                bgcolor: '#fbbf24',
                color: '#1e3a5f',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 12,
                boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
                '&:hover': { bgcolor: '#f59e0b' }
              }}
            >
              Nueva Consulta
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* NARA Terminal - Centrado, encima de herramientas */}
      {(loadingBriefing || naraBriefing) && (
        <NaraTerminalCard
          label="nara@briefing ~ paciente"
          loading={loadingBriefing}
          loadingText="Analizando paciente"
        >
          {naraBriefing && <NaraTypewriter briefing={naraBriefing} />}
        </NaraTerminalCard>
      )}

      {/* Sección: Herramientas IA */}
      <Typography sx={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.1em', color: '#94a3b8', mb: 1.5, textAlign: 'center'
      }}>
        Herramientas IA
      </Typography>

      {/* Quick Action Cards - Centradas */}
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: 1.5, mb: 4
      }}>
        {accionesRapidas.map((item) => (
          <Box
            key={item.id}
            onClick={item.action}
            sx={{
              p: 1.5, borderRadius: 2.5,
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer', textAlign: 'center',
              position: 'relative', overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
              width: { xs: 'calc(50% - 6px)', sm: 'auto' },
              minWidth: { sm: 110 }, maxWidth: { sm: 130 },
              '&::before': {
                content: '""', position: 'absolute',
                top: 0, left: 0, right: 0, height: 3,
                background: item.gradient
              },
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: `0 20px 40px rgba(0,0,0,0.12), 0 8px 16px ${item.shadowColor}`
              },
              '&:active': { transform: 'translateY(-2px) scale(0.98)' },
              '&:focus-visible': { outline: `3px solid ${item.shadowColor}`, outlineOffset: 2 }
            }}
          >
            <Box sx={{
              width: 38, height: 38, borderRadius: 2,
              background: item.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', mx: 'auto', mb: 1,
              boxShadow: `0 6px 16px ${item.shadowColor}`,
              '& svg': { fontSize: 20 }
            }}>
              {item.icon}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f', lineHeight: 1.2 }}>
              {item.nombre}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.3 }}>
              {item.descripcion}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Timeline y Antecedentes - Centrados y compactos */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
        {/* Timeline */}
        <Box sx={{ width: { xs: '100%', md: 420 }, maxWidth: '100%' }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: '#94a3b8', mb: 1, pl: 0.5
          }}>
            Historial Clínico
          </Typography>
          <Box sx={{ ...glassPanel('linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'), p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1.5, mr: 1,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                '& svg': { fontSize: 16, color: '#fff' }
              }}>
                <TimelineIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
                Historial Clínico
              </Typography>
            </Box>
            {consultasRecientes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <DescriptionIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 0.5 }} />
                <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>No hay registros aún</Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {consultasRecientes.map((item, idx) => (
                  <ListItem key={idx} sx={{
                    bgcolor: 'rgba(248, 250, 252, 0.8)',
                    borderRadius: 1.5,
                    mb: 0.75,
                    py: 0.75,
                    border: '1px solid rgba(226, 232, 240, 0.5)',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.9)', transform: 'translateX(4px)' }
                  }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar sx={{
                        bgcolor: item.tipo === 'consulta' ? '#6366f1' : item.tipo === 'estudio' ? '#f59e0b' : '#10b981',
                        width: 30, height: 30,
                        boxShadow: item.tipo === 'consulta'
                          ? '0 4px 12px rgba(99, 102, 241, 0.3)'
                          : item.tipo === 'estudio'
                          ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                          : '0 4px 12px rgba(16, 185, 129, 0.3)',
                        '& svg': { fontSize: 15 }
                      }}>
                        {item.tipo === 'consulta' ? <MedicalIcon /> : item.tipo === 'estudio' ? <ImageIcon /> : <DescriptionIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>{item.titulo}</Typography>}
                      secondary={<Typography sx={{ fontSize: 10, color: '#64748b' }}>{new Date(item.fecha).toLocaleDateString('es-MX')} - {item.descripcion?.substring(0, 40)}...</Typography>}
                    />
                    <Chip
                      label={item.estado}
                      size="small"
                      sx={{
                        fontSize: 9, fontWeight: 600, height: 20,
                        bgcolor: item.estado === 'firmada' ? '#dcfce7' : '#fef3c7',
                        color: item.estado === 'firmada' ? '#166534' : '#92400e'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Antecedentes */}
        <Box sx={{ width: { xs: '100%', md: 340 }, maxWidth: '100%' }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: '#94a3b8', mb: 1, pl: 0.5
          }}>
            Antecedentes
          </Typography>
          <Box sx={{ ...glassPanel('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'), p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1.5, mr: 1,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                '& svg': { fontSize: 16, color: '#fff' }
              }}>
                <FolderIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
                Antecedentes
              </Typography>
            </Box>
            {expedienteActual ? (
              <Box>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(248, 250, 252, 0.8)', borderRadius: 1.5, mb: 1, border: '1px solid rgba(226, 232, 240, 0.5)' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                    Heredo-familiares
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#475569' }}>
                    {Object.keys(expedienteActual.antecedentes_heredo_familiares || {}).length > 0
                      ? JSON.stringify(expedienteActual.antecedentes_heredo_familiares)
                      : 'Sin registrar'}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(248, 250, 252, 0.8)', borderRadius: 1.5, mb: 1, border: '1px solid rgba(226, 232, 240, 0.5)' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                    Patológicos
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#475569' }}>
                    {Object.keys(expedienteActual.antecedentes_personales_patologicos || {}).length > 0
                      ? JSON.stringify(expedienteActual.antecedentes_personales_patologicos)
                      : 'Sin registrar'}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  sx={{
                    mt: 0.5, borderRadius: 2, fontWeight: 600, fontSize: 11,
                    color: '#64748b',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)', color: '#6366f1' }
                  }}
                >
                  Editar Antecedentes
                </Button>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12 }}>
                Este paciente no tiene expediente activo.
                <Button size="small" sx={{ ml: 2 }}>Crear Expediente</Button>
              </Alert>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Calcular edad
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 0;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  // Si hay error de acceso
  if (error) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <WarningIcon sx={{ fontSize: 80, color: '#f59e0b', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Acceso Restringido</Typography>
        <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Volver al Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 2, md: 4 } }}>
      {/* Header principal */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <HospitalIcon sx={{ fontSize: 40, color: '#1e3a5f', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e3a5f' }}>
              Expediente Médico
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de gestión de expedientes clínicos
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => setDialogAgenda(true)}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: 2,
              fontWeight: 600,
              px: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              }
            }}
          >
            Agenda de Citas
          </Button>
          <Chip
            icon={<VerifiedIcon />}
            label="NOM-024 | HIPAA"
            color="success"
            variant="outlined"
          />
          <IconButton onClick={cargarPacientes}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Contenido según tab */}
      {tabActual === 0 && renderListaPacientes()}
      {tabActual === 1 && pacienteSeleccionado && renderExpediente()}

      {/* Dialog Nuevo Paciente - Diseño mejorado */}
      <Dialog
        open={dialogNuevoPaciente}
        onClose={() => setDialogNuevoPaciente(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "#f8fafc",
            boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
            border: "none",
            overflow: "hidden"
          }
        }}
      >
        {/* Header con gradiente */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            color: "#fff",
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PersonIcon sx={{ fontSize: 26, color: "#fbbf24" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.5 }}>
              Nuevo Paciente
            </Typography>
          </Box>
          <Chip
            icon={<HospitalIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
            label="EMR"
            size="small"
            sx={{
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 11
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Sección: Datos Personales */}
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
            <Typography sx={{ fontWeight: 600, color: "#1e3a5f", fontSize: 13, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 18, color: "#6366f1" }} />
              Datos Personales
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  placeholder="Ej: Juan Pérez García"
                  value={nuevoPaciente.nombre_completo}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, nombre_completo: e.target.value })}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Nacimiento"
                  value={nuevoPaciente.fecha_nacimiento}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, fecha_nacimiento: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Sexo"
                  value={nuevoPaciente.sexo}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, sexo: e.target.value })}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                >
                  <MenuItem value="masculino">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                      Masculino
                    </Box>
                  </MenuItem>
                  <MenuItem value="femenino">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ec4899' }} />
                      Femenino
                    </Box>
                  </MenuItem>
                  <MenuItem value="otro">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                      Otro
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* Sección: Identificación */}
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
            <Typography sx={{ fontWeight: 600, color: "#1e3a5f", fontSize: 13, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ fontSize: 18, color: "#10b981" }} />
              Identificación
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="País"
                  select
                  value={nuevoPaciente.pais}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, pais: e.target.value, documento_identidad: '' })}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                >
                  {Object.entries(documentosPorPais).map(([code, config]) => (
                    <MenuItem key={code} value={code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 16 }}>
                          {code === 'MX' ? '🇲🇽' : code === 'CO' ? '🇨🇴' : code === 'US' ? '🇺🇸' : code === 'ES' ? '🇪🇸' : code === 'PE' ? '🇵🇪' : code === 'AR' ? '🇦🇷' : code === 'CL' ? '🇨🇱' : code === 'EC' ? '🇪🇨' : code === 'VE' ? '🇻🇪' : '🌎'}
                        </Typography>
                        {config.nombre}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={documentosPorPais[nuevoPaciente.pais]?.documento || 'Documento'}
                  placeholder={documentosPorPais[nuevoPaciente.pais]?.placeholder || ''}
                  value={nuevoPaciente.documento_identidad}
                  onChange={(e) => {
                    const value = nuevoPaciente.pais === 'MX' ? e.target.value.toUpperCase() : e.target.value;
                    setNuevoPaciente({ ...nuevoPaciente, documento_identidad: value });
                  }}
                  inputProps={{ maxLength: documentosPorPais[nuevoPaciente.pais]?.maxLength || 20 }}
                  size="small"
                  helperText={nuevoPaciente.documento_identidad ?
                    `${nuevoPaciente.documento_identidad.length}/${documentosPorPais[nuevoPaciente.pais]?.maxLength || 20}` :
                    "Opcional"
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      fontFamily: 'monospace',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Tipo de Sangre"
                  select
                  value={nuevoPaciente.tipo_sangre || ''}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, tipo_sangre: e.target.value })}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                >
                  <MenuItem value=""><em>No especificado</em></MenuItem>
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* Sección: Contacto */}
          <Box sx={{ px: 3, py: 2, background: "#fff" }}>
            <Typography sx={{ fontWeight: 600, color: "#1e3a5f", fontSize: 13, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicalIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
              Contacto
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  placeholder="Ej: 55 1234 5678"
                  value={nuevoPaciente.telefono}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, telefono: e.target.value })}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={nuevoPaciente.correo_electronico}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, correo_electronico: e.target.value })}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: '#f8fafc',
                      '&:hover': { background: '#f1f5f9' },
                      '&.Mui-focused': { background: '#fff' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", background: "#fff" }}>
          <Button
            onClick={() => setDialogNuevoPaciente(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCrearPaciente}
            disabled={!nuevoPaciente.nombre_completo || !nuevoPaciente.fecha_nacimiento}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
              boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #2d5a87 0%, #3d6a97 100%)",
                boxShadow: "0 6px 16px rgba(30, 58, 95, 0.4)"
              },
              "&:disabled": {
                background: "#e2e8f0",
                color: "#94a3b8"
              }
            }}
          >
            Crear Paciente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nueva Consulta */}
      <ConsultaForm
        open={dialogNuevaConsulta}
        onClose={() => {
          setDialogNuevaConsulta(false);
          setDatosVozParaConsulta(null);
        }}
        expedienteId={expedienteActual?.id}
        paciente={pacienteSeleccionado}
        userId={userId}
        datosIniciales={datosVozParaConsulta}
        onConsultaCreated={(consulta) => {
          setSnackbar({ open: true, message: 'Consulta creada correctamente', severity: 'success' });
          setDatosVozParaConsulta(null);
          handleSeleccionarPaciente(pacienteSeleccionado);
        }}
      />

      {/* Dialog Transcripción de Consulta */}
      <TranscripcionConsulta
        open={dialogTranscripcion}
        onClose={() => setDialogTranscripcion(false)}
        consultaId={null}
        paciente={pacienteSeleccionado}
        userId={userId}
        onTranscripcionComplete={(data) => {
          setSnackbar({ open: true, message: 'Transcripción completada', severity: 'success' });
          console.log('Transcripción:', data);
        }}
      />

      {/* Dialog Analizador de Imágenes */}
      <AnalizadorImagenes
        open={dialogAnalizadorImagenes}
        onClose={() => setDialogAnalizadorImagenes(false)}
        expedienteId={expedienteActual?.id}
        paciente={pacienteSeleccionado}
        userId={userId}
        onAnalisisComplete={(data) => {
          setSnackbar({ open: true, message: 'Análisis de imagen completado', severity: 'success' });
          console.log('Análisis:', data);
        }}
      />

      {/* Dialog Agenda de Citas */}
      <AgendaCitas
        open={dialogAgenda}
        onClose={() => setDialogAgenda(false)}
        userId={userId}
        onCitaSelected={(cita) => {
          // Opcional: buscar al paciente de la cita seleccionada
          setSnackbar({ open: true, message: `Cita seleccionada: ${cita.paciente_nombre}`, severity: 'info' });
        }}
      />

      {/* Dialog Consulta por Voz */}
      <ConsultaVoz
        open={dialogConsultaVoz}
        onClose={() => setDialogConsultaVoz(false)}
        paciente={pacienteSeleccionado}
        userId={userId}
        expedienteId={expedienteActual?.id}
        onDatosExtraidos={(datos) => {
          setDialogConsultaVoz(false);
          setDatosVozParaConsulta(datos);
          setDialogNuevaConsulta(true);
          setSnackbar({ open: true, message: 'Datos extraídos por IA. Revisa el formulario prellenado.', severity: 'success' });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EMRDashboard;
